import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete';
import { FormDateInput } from '~/components/FormComponents/FormDateInput';
import { FormInput } from '~/components/FormComponents/FormInput';
import {
  ALL_GENERAL_DOC_TYPES,
  DISABLED_SERIES_DOCUMENT_TYPES,
  EXPIRY_DOCUMENT_TYPES,
  FOREIGN_DOCUMENT_TYPES,
  UKRAINIAN_DOCUMENT_TYPES,
} from '~/constants/docs.ts';
import { useDocumentsQuery } from '~/queries/useDocumentsQuery';
import { addTenYears } from '../../forms/OwnershipStructure/utils/utils.tsx';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { UKRAINE_CODE } from '~/constants/common.ts';

interface IDocumentInfoProps {
  citizenshipCountryPath: string;
  residenceCountryPath: string;
  section?: string;
}

export const DocumentInfo = ({
  citizenshipCountryPath,
  residenceCountryPath,
  section,
}: IDocumentInfoProps) => {
  const { personalDocOptions } = useDocumentsQuery();
  const { control, setValue } = useFormContext();
  const documentPath = section ? section + '.documents' : 'documents';
  const [citizenshipCountry, residenceCountry, documentType] = useWatch({
    control,
    name: [citizenshipCountryPath, residenceCountryPath, `${documentPath}.documentType`],
  });

  const isUkraineCitizen = citizenshipCountry?.value === UKRAINE_CODE;
  const isUkraineResident = residenceCountry?.value === UKRAINE_CODE;
  const showExpiryDate = EXPIRY_DOCUMENT_TYPES.includes(+documentType?.value);
  const documentsArray =
    isUkraineCitizen && isUkraineResident
      ? UKRAINIAN_DOCUMENT_TYPES
      : isUkraineCitizen || isUkraineResident
        ? ALL_GENERAL_DOC_TYPES
        : FOREIGN_DOCUMENT_TYPES;

  const documentOptions = useMemo(
    () => personalDocOptions.filter((doc) => documentsArray.includes(+doc.value)),
    [personalDocOptions, documentsArray]
  );

  const handleSetDocumentType = (option: TAutocompleteOption | TAutocompleteOption[] | null) => {
    if ((option as TAutocompleteOption)?.value) {
      setValue(`${documentPath}.registryRecord`, null);
      setValue(`${documentPath}.documentSeries`, null);
      setValue(`${documentPath}.documentNumber`, null);
      setValue(`${documentPath}.issueDate`, null);
      setValue(`${documentPath}.documentExpiryDate`, null);
      setValue(`${documentPath}.issuingAuthority`, null);
    }
  };

  const handleSetDate = (date: string | Date | null) => {
    setValue(`${documentPath}.issueDate`, date);

    if (showExpiryDate && date && EXPIRY_DOCUMENT_TYPES.includes(+documentType?.value)) {
      setValue(`${documentPath}.documentExpiryDate`, addTenYears(date as string));
    }
  };

  const disabledDocumentSeries = DISABLED_SERIES_DOCUMENT_TYPES.includes(
    Number(documentType?.value)
  );

  useEffect(() => {
    if (disabledDocumentSeries) {
      setValue(`${documentPath}.documentSeries`, null);
    }
  }, [disabledDocumentSeries]);

  return (
    <>
      <FormGridItem>
        <FormAutocomplete
          options={documentOptions}
          onChange={handleSetDocumentType}
          name={`${documentPath}.documentType`}
          label="Тип документа"
          inputProps={{
            placeholder: 'Введіть тип документа',
          }}
        />
      </FormGridItem>
      <FormGridItem>
        <FormInput
          name={`${documentPath}.registryRecord`}
          label="Запис у реєстрі"
          placeholder="Введіть запис у реєстрі"
          fullWidth
          inputProps={{
            maxLength: 200,
            minLength: 1,
          }}
        />
      </FormGridItem>
      <FormGridItem>
        <FormInput
          name={`${documentPath}.documentSeries`}
          label="Серія документа"
          placeholder="Введіть серію документа"
          disabled={disabledDocumentSeries}
          fullWidth
          inputProps={{
            maxLength: 200,
            minLength: 1,
          }}
        />
      </FormGridItem>
      <FormGridItem>
        <FormInput
          name={`${documentPath}.documentNumber`}
          label="Номер документа"
          placeholder="Введіть номер документа"
          fullWidth
          inputProps={{
            maxLength: 200,
            minLength: 1,
          }}
        />
      </FormGridItem>
      <FormGridItem>
        <FormDateInput
          name={`${documentPath}.issueDate`}
          label="Дата видачі документа"
          placeholder="Введіть дату видачі документа"
          onChange={handleSetDate}
          isCurrentMaxDate
          disableFormOnChange
        />
      </FormGridItem>
      {showExpiryDate && (
        <FormGridItem>
          <FormDateInput
            name={`${documentPath}.documentExpiryDate`}
            label="Дата закінчення документа"
            disabled
            placeholder="Введіть дату закінчення документа"
          />
        </FormGridItem>
      )}
      <FormGridItem>
        <FormInput
          name={`${documentPath}.issuingAuthority`}
          label="Назва органу, що видав документ"
          placeholder="Введіть назву органу, що видав документ"
          fullWidth
          inputProps={{
            maxLength: 120,
            minLength: 1,
          }}
        />
      </FormGridItem>
      {showExpiryDate && <FormGridItem />}
    </>
  );
};
